// Import all auction-related atoms
import { auctionAtom } from "@/src/state/auctionAtom";
import { auctionAtomCopy } from "@/src/state/auctionAtomCopy";
import { auctionStepRegnoMileageStateAtom } from "@/src/state/auctionWizard/auctionStepsAtom";
import { auctionWizardStateAtom } from "@/src/state/auctionWizard/auctionWizardAtom";
import { auctionWizardUnknownCheckBoxAtom } from "@/src/state/auctionWizard/auctionWizardUnknownCheckBoxAtom";
import {
  nettixBikeTypeId,
  nettixMakeModelIdAtom
} from "@/src/state/auctionWizard/nettixMakeModelIdAtom";
import { beelyAttributionAtom } from "@/src/state/beely/beelyAttributionAtom";
import { createAuctionAtom } from "@/src/state/createAuctionAtom";
import { showStepSummaryButtonAtom } from "@/src/state/showStepSummaryButtonAtom";
import { vehicleBasicInfoStateAtom } from "@/src/state/vehicleBasicInfo";

import { ConsumerAuctionDTO } from "@/src/types/types";

import { useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";

/**
 * Clears all auction-related localStorage/sessionStorage entries.
 * Safe to call outside React — no Jotai atom resets, no re-renders.
 * Use when navigating AWAY from the wizard so that a future wizard
 * session starts with clean persisted state.
 */
export const clearAuctionLocalStorage = () => {
  localStorage.removeItem("auctionAtom");
  localStorage.removeItem("auctionAtomCopy");
  localStorage.removeItem("createAuctionAtom");
  localStorage.removeItem("auctionWizardStateAtom");
  localStorage.removeItem("auctionWizardUnknownCheckBoxAtom");
  localStorage.removeItem("auctionStepRegnoMileageStateAtom");
  localStorage.removeItem("vehicleBasicInfoStateAtom");
  localStorage.removeItem("nettixMakeModelIdAtom");
  sessionStorage.removeItem("beelyAttributionAtom");
};

/**
 * Custom hook to clear all auction-related state atoms
 * Use this when starting a new auction to ensure clean state
 *
 * @returns clearAllAuctionState - Function to clear all auction atoms
 */
export const useClearAuctionState = () => {
  const resetAuction = useResetAtom(auctionAtom);
  const setAuction = useSetAtom(auctionAtom);
  const resetAuctionCopy = useResetAtom(auctionAtomCopy);
  const resetCreateAuction = useResetAtom(createAuctionAtom);
  const resetAuctionWizardState = useResetAtom(auctionWizardStateAtom);
  const resetAuctionWizardUnknownCheckBox = useResetAtom(
    auctionWizardUnknownCheckBoxAtom
  );
  const resetAuctionStepRegnoMileageState = useResetAtom(
    auctionStepRegnoMileageStateAtom
  );
  const resetVehicleBasicInfoState = useResetAtom(vehicleBasicInfoStateAtom);
  const resetNettixMakeModelId = useResetAtom(nettixMakeModelIdAtom);
  const resetNettixBikeTypeId = useResetAtom(nettixBikeTypeId);
  const resetBeelyAttribution = useResetAtom(beelyAttributionAtom);
  const resetShowStepSummaryButton = useResetAtom(showStepSummaryButtonAtom);

  const clearAllAuctionState = () => {
    clearAuctionLocalStorage();

    // Explicitly set auctionAtom to empty object to prevent StepFinished flash
    setAuction({} as Partial<ConsumerAuctionDTO>);

    // Then reset all other atoms (this will read from cleared localStorage)
    resetAuction();
    resetAuctionCopy();
    resetCreateAuction();
    resetAuctionWizardState();
    resetAuctionWizardUnknownCheckBox();
    resetAuctionStepRegnoMileageState();
    resetVehicleBasicInfoState();
    resetNettixMakeModelId();
    resetNettixBikeTypeId();
    resetBeelyAttribution();
    resetShowStepSummaryButton();
  };

  return { clearAllAuctionState };
};
